// src/pages/Contact.jsx
import React, { useState } from 'react';
import {
  Box, Container, Typography, Grid, Card, CardContent,
  Chip, Button, Avatar, Stack, Paper, TextField,
  alpha, useTheme, CircularProgress,
  MenuItem, Select, FormControl, InputLabel,
} from '@mui/material';
import {
  Email, Phone, LocationOn, AccessTime, Send,
  WhatsApp, CheckCircle, Public,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 40 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6, delay },
});

const Contact = () => {
  const theme = useTheme();
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [form, setForm] = useState({
    name: '', email: '', phone: '', company: '',
    subject: '', message: '', userType: '',
  });

  const handleChange = (e) => setForm((p) => ({ ...p, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error('Please fill in all required fields');
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API_BASE}/contact`, {
        name:    form.name,
        email:   form.email,
        phone:   form.phone,
        subject: form.subject || (form.userType ? `${form.userType} Enquiry` : 'Contact Form'),
        message: form.message,
      });
      setSent(true);
      toast.success("Message sent! We'll respond within 24 hours.");
      setForm({ name: '', email: '', phone: '', company: '', subject: '', message: '', userType: '' });
      setTimeout(() => setSent(false), 5000);
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Failed to send message. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const contactInfo = [
    {
      icon: <LocationOn sx={{ fontSize: 28 }} />, color: '#4caf50',
      title: 'Our Office',
      lines: [
        'Old No: 55, New No: 123',
        'Eldams Road, Teynampet',
        'Chennai – 600018',
        'Tamil Nadu, India',
      ],
    },
    {
      icon: <Email sx={{ fontSize: 28 }} />, color: '#2196f3',
      title: 'Email Us',
      lines: ['Support@smartwaveintl.com'],
      action: { label: 'Send Email', href: 'mailto:Support@smartwaveintl.com' },
    },
    {
      icon: <Phone sx={{ fontSize: 28 }} />, color: '#ff9800',
      title: 'Call Us',
      lines: ['+91 8925851195', '+91 8925851196'],
      action: { label: 'Call Now', href: 'tel:+918925851195' },
    },
    {
      icon: <WhatsApp sx={{ fontSize: 28 }} />, color: '#25d366',
      title: 'WhatsApp',
      lines: ['+91 8925851195', 'Instant response during business hours'],
      action: { label: 'Chat on WhatsApp', href: 'https://wa.me/918925851195?text=Hello%2C%20I%20am%20interested%20in%20your%20fruit%20export%20services.' },
    },
    {
      icon: <AccessTime sx={{ fontSize: 28 }} />, color: '#9c27b0',
      title: 'Business Hours',
      lines: ['Mon – Fri: 9:00 AM – 6:00 PM IST', 'Sat: 9:00 AM – 2:00 PM IST', 'Sun: Closed'],
    },
  ];


  return (
    <Box sx={{ backgroundColor: 'grey.50', minHeight: '100vh' }}>

      {/* ── Hero ── */}
      <Box sx={{
        background: 'linear-gradient(135deg, #0d2f12 0%, #1b5e20 40%, #2e7d32 100%)',
        color: 'white', py: { xs: 8, md: 12 }, position: 'relative', overflow: 'hidden',
      }}>
        {[300, 400, 200].map((size, i) => (
          <Box key={i} sx={{
            position: 'absolute', borderRadius: '50%',
            backgroundColor: alpha('#4caf50', 0.07),
            width: size, height: size,
            top: ['-10%', '35%', '50%'][i], left: ['65%', '-8%', '80%'][i],
            pointerEvents: 'none',
          }} />
        ))}
        <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 1, textAlign: 'center' }}>
          <motion.div {...fadeUp()}>
            <Chip label="GET IN TOUCH" sx={{ bgcolor: alpha('#ffeb3b', 0.2), color: '#ffeb3b', fontWeight: 700, mb: 2 }} />
            <Typography variant="h2" fontWeight={800} sx={{ mb: 2, fontSize: { xs: '2rem', md: '3.5rem' } }}>
              Contact
              <Box component="span" sx={{ background: 'linear-gradient(90deg,#ffeb3b,#ff9800)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', ml: 1 }}>
                Smartwave
              </Box>
            </Typography>
            <Typography variant="h6" sx={{ opacity: 0.85, maxWidth: 600, mx: 'auto', fontWeight: 400 }}>
              Whether you're a buyer, seller, or just curious — our team is here to help you start trading globally.
            </Typography>
          </motion.div>
        </Container>
      </Box>

      {/* ── Contact Info Cards ── */}
      <Container maxWidth="xl" sx={{ py: { xs: 6, md: 8 }, mt: -4, position: 'relative', zIndex: 2 }}>
        <Grid container spacing={3}>
          {contactInfo.map((info, i) => (
            <Grid item xs={12} sm={6} md={3} key={i}>
              <motion.div {...fadeUp(i * 0.1)}>
                <Card sx={{
                  p: 1, borderRadius: 4, height: '100%',
                  border: `2px solid ${alpha(info.color, 0.15)}`,
                  boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
                  transition: 'all 0.3s',
                  '&:hover': { transform: 'translateY(-4px)', borderColor: info.color },
                }} elevation={0}>
                  <CardContent sx={{ p: 3 }}>
                    <Avatar sx={{ bgcolor: alpha(info.color, 0.12), color: info.color, width: 56, height: 56, mb: 2 }}>
                      {info.icon}
                    </Avatar>
                    <Typography variant="h6" fontWeight="bold" gutterBottom>{info.title}</Typography>
                    <Stack spacing={0.5} sx={{ mb: info.action ? 2 : 0 }}>
                      {info.lines.map((line, j) => (
                        <Typography key={j} variant="body2" color="text.secondary">{line}</Typography>
                      ))}
                    </Stack>
                    {info.action && (
                      <Button
                        variant="outlined"
                        size="small"
                        href={info.action.href}
                        sx={{ borderColor: info.color, color: info.color, borderRadius: 2, mt: 1 }}
                      >
                        {info.action.label}
                      </Button>
                    )}
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* ── Contact Form + Map ── */}
      <Container maxWidth="xl" sx={{ py: { xs: 4, md: 8 } }}>
        <Grid container spacing={4}>

          {/* Form */}
          <Grid item xs={12} md={7}>
            <motion.div {...fadeUp()}>
              <Paper sx={{ p: { xs: 3, md: 5 }, borderRadius: 4, border: '1px solid', borderColor: 'divider' }} elevation={0}>
                <Typography variant="h4" fontWeight="bold" gutterBottom>Send Us a Message</Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
                  Fill in the form below and our team will get back to you within 24 business hours.
                </Typography>

                {sent ? (
                  <Box sx={{ textAlign: 'center', py: 6 }}>
                    <Avatar sx={{ bgcolor: 'success.light', width: 80, height: 80, mx: 'auto', mb: 2 }}>
                      <CheckCircle sx={{ fontSize: 48, color: 'success.main' }} />
                    </Avatar>
                    <Typography variant="h5" fontWeight="bold" gutterBottom color="success.main">Message Sent!</Typography>
                    <Typography variant="body1" color="text.secondary">
                      Thank you for reaching out. We'll respond to <strong>{form.email || 'your email'}</strong> within 24 hours.
                    </Typography>
                  </Box>
                ) : (
                  <Box component="form" onSubmit={handleSubmit}>
                    <Grid container spacing={2.5}>
                      <Grid item xs={12} sm={6}>
                        <TextField fullWidth label="Full Name *" name="name" value={form.name} onChange={handleChange} required />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField fullWidth label="Email Address *" name="email" type="email" value={form.email} onChange={handleChange} required />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField fullWidth label="Phone Number" name="phone" value={form.phone} onChange={handleChange} placeholder="+91 XXXXX XXXXX" />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField fullWidth label="Company Name" name="company" value={form.company} onChange={handleChange} />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <FormControl fullWidth>
                          <InputLabel>I am a...</InputLabel>
                          <Select name="userType" value={form.userType} onChange={handleChange} label="I am a...">
                            <MenuItem value="buyer">Buyer / Importer</MenuItem>
                            <MenuItem value="seller">Seller / Exporter</MenuItem>
                            <MenuItem value="logistics">Logistics Partner</MenuItem>
                            <MenuItem value="other">Other</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <FormControl fullWidth>
                          <InputLabel>Subject</InputLabel>
                          <Select name="subject" value={form.subject} onChange={handleChange} label="Subject">
                            <MenuItem value="general">General Enquiry</MenuItem>
                            <MenuItem value="trade">Trade Partnership</MenuItem>
                            <MenuItem value="support">Technical Support</MenuItem>
                            <MenuItem value="payment">Payment Issue</MenuItem>
                            <MenuItem value="shipment">Shipment Query</MenuItem>
                            <MenuItem value="demo">Request a Demo</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth multiline rows={5}
                          label="Your Message *"
                          name="message" value={form.message} onChange={handleChange}
                          required placeholder="Tell us how we can help you..."
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Button
                          type="submit" variant="contained" size="large" fullWidth
                          endIcon={sending ? <CircularProgress size={18} color="inherit" /> : <Send />}
                          disabled={sending}
                          sx={{ py: 1.8, borderRadius: 3, fontWeight: 'bold', fontSize: '1rem' }}
                        >
                          {sending ? 'Sending...' : 'Send Message'}
                        </Button>
                      </Grid>
                    </Grid>
                  </Box>
                )}
              </Paper>
            </motion.div>
          </Grid>

          {/* Map */}
          <Grid item xs={12} md={5}>
            <motion.div {...fadeUp(0.15)}>
              <Paper sx={{ borderRadius: 4, overflow: 'hidden', border: '1px solid', borderColor: 'divider' }} elevation={0}>
                {/* Static map image — no iframe, never blocked */}
                <Box
                  component="a"
                  href="https://www.google.com/maps/search/Eldams+Road+Teynampet+Chennai+600018"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ display: 'block', position: 'relative', overflow: 'hidden', cursor: 'pointer' }}
                >
                  <Box
                    component="img"
                    src={`https://staticmap.openstreetmap.de/staticmap.php?center=13.049,80.2451&zoom=15&size=600x300&markers=13.049,80.2451,ol-marker`}
                    alt="Smartwave International location map"
                    onError={e => {
                      // Fallback to styled map card if image fails
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                    sx={{ width: '100%', height: 300, objectFit: 'cover', display: 'block' }}
                  />
                  {/* Fallback map visual (shown if image fails to load) */}
                  <Box sx={{
                    display: 'none', height: 300, flexDirection: 'column',
                    alignItems: 'center', justifyContent: 'center',
                    background: 'linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 50%, #a5d6a7 100%)',
                    position: 'relative',
                  }}>
                    <Box sx={{
                      width: 80, height: 80, borderRadius: '50%',
                      bgcolor: 'rgba(46,125,50,0.15)', border: '2px solid #2e7d32',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2,
                    }}>
                      <LocationOn sx={{ fontSize: 40, color: 'primary.main' }} />
                    </Box>
                    <Typography variant="subtitle1" fontWeight={700} color="primary.dark">Chennai, Tamil Nadu</Typography>
                    <Typography variant="body2" color="text.secondary">Teynampet, 600018</Typography>
                    {/* Fake map grid lines */}
                    {[...Array(5)].map((_, i) => (
                      <Box key={i} sx={{
                        position: 'absolute', left: 0, right: 0,
                        top: `${20 + i * 15}%`, height: '1px',
                        bgcolor: 'rgba(46,125,50,0.1)',
                      }} />
                    ))}
                    {[...Array(5)].map((_, i) => (
                      <Box key={i} sx={{
                        position: 'absolute', top: 0, bottom: 0,
                        left: `${10 + i * 20}%`, width: '1px',
                        bgcolor: 'rgba(46,125,50,0.1)',
                      }} />
                    ))}
                  </Box>
                  {/* "Click to open" overlay */}
                  <Box sx={{
                    position: 'absolute', inset: 0,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    opacity: 0, transition: 'opacity 0.2s',
                    bgcolor: 'rgba(0,0,0,0.35)',
                    '&:hover': { opacity: 1 },
                  }}>
                    <Chip
                      icon={<LocationOn />}
                      label="Open in Google Maps"
                      sx={{ bgcolor: '#fff', fontWeight: 700, fontSize: '0.9rem', px: 1 }}
                    />
                  </Box>
                </Box>
                <Box sx={{ p: 2.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                    <LocationOn sx={{ color: 'primary.main', mt: 0.2 }} />
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="subtitle2" fontWeight="bold">Smartwave International Pvt. Ltd.</Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        Old No: 55, New No: 123, Eldams Road,<br />
                        Teynampet, Chennai – 600018, Tamil Nadu, India
                      </Typography>
                      <Button
                        size="small" variant="contained" color="primary"
                        href="https://www.google.com/maps/search/Eldams+Road+Teynampet+Chennai+600018"
                        target="_blank" rel="noopener noreferrer"
                        startIcon={<LocationOn />}
                      >
                        Open in Google Maps
                      </Button>
                    </Box>
                  </Box>
                </Box>
              </Paper>
            </motion.div>
          </Grid>
        </Grid>
      </Container>

    </Box>
  );
};

export default Contact;